import {Injectable} from '@angular/core';
import {ICRUDRecord, ICRUDRecordRead, ICRUDRecordReadReply, ISceneBlock, IResource, IResourceUpload, IPlayerProps} from "../interfaces";
import {actionAddResources, actionAddScene} from "../store/actions/user.actions";
import {Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {AppService} from "./app.service";
import {LocalStoreService} from "./local-store.service";
import {StoreSelectorsService} from "./store-selectors.service";
import {Compbaser} from '../components/compbaser';
import {appData, isNullOrUndef, preprocessSceneRecord, preProcessSceneTree, SUPPORTED_FILE_TYPES} from '../common';
import * as xml2js from 'xml2js';

@Injectable({
    providedIn: 'root'
})
export class ImportService extends Compbaser {

    resourceShortcuts;
    progress = new Subject();
    progress$ = this.progress.asObservable();

    constructor(
        private appService: AppService,
        private localStoreService: LocalStoreService,
        private storeSelector: StoreSelectorsService,
    ) {
        super();
        this.cod(
            this.storeSelector.getResources(false).subscribe((resources: IResource[]) => {
                this.resourceShortcuts = {};
                resources.forEach(r => {
                    if (r.shortcut_business_id && r.shortcut_resource_id && r.shortcut_business_id != "0" && r.shortcut_resource_id != "0") {
                        const key = r.shortcut_business_id + '.' + r.shortcut_resource_id;
                        this.resourceShortcuts[key] = r.resource_id;
                    }
                });
            })
        );
    }

    async importScene(i_businessId, sceneId, newLabel, privateChild = null, showDetails = false, labelColor = '') {
        try {
            const sceneData = await this.getRemoteScene(i_businessId, sceneId);
            if (!sceneData) {
                return null;
            }

            const players = sceneData.Player.Data[0].Scene[0].Players[0].Player;
            if (isNullOrUndef(players)) {
                return null;
            }
            await this.importPlayers(players, i_businessId, privateChild, showDetails, labelColor);

            const newSceneId = await this.importPrebuiltScene(sceneData, newLabel, privateChild);
            return newSceneId;

        } catch (ex) {
            console.log(ex);
            return null;
        }
    }

    async importPrebuiltScene(sceneData, newLabel = null, privateChild = null) {
        return new Promise<string>((resolve, error) => {
            if (newLabel != null) {
                sceneData.Player.$.label = newLabel;
            }
            const studioId = this.localStoreService.getStorageConfig().studioId;
            const saveRecord = preprocessSceneRecord(sceneData, null, studioId, 'add');

            /** create scene and get inserted id */
            this.appService.crudRecord(saveRecord).then((i_result: ICRUDRecord) => {
                const newSceneId = String(i_result.serverReply.id);
                sceneData.Player.$.id = newSceneId;
                sceneData.Player.$.lastModified = `${new Date().getTime()}`;
                sceneData.Player.$.changelist_id = String(i_result.serverReply.changelist_id);

                if (!isNullOrUndef(privateChild)) {
                    const updateRecord: ICRUDRecord = {
                        studioId,
                        command: 'update',
                        primary: 'player_data_id',
                        table: 'player_data',
                        body: {
                            private_child: privateChild
                        },
                        id: newSceneId
                    };
                    this.appService.crudRecord(updateRecord);
                    sceneData.Player.$.private_child = privateChild;
                }

                preProcessSceneTree(sceneData, newSceneId);
                this.storeSelector.dispatch(actionAddScene({player: sceneData}));
                resolve(newSceneId);
            }).catch((err) => error(err));
        });
    }

    async importPlayers(players, i_businessId, privateChild = null, showDetails = false, labelColor = '', skipIds = null) {
        for (const player of players) {
            if (skipIds && skipIds.includes(player.$.id)) continue;
            if (player.$.player == this.storeSelector.blockLabels.BLOCKCODE_SCENE.id) {
                const oldSceneId = player.$.src;
                const newSceneId = await this.importScene(i_businessId, oldSceneId, 'imported scene ' + oldSceneId, privateChild, showDetails, labelColor);
                player.$.src = newSceneId;

            } else if (player.$.player == this.storeSelector.blockLabels.PLAYLIST.id) {  // import scenes & resources of playlist
                const playlistPlayers = player.Data[0].Playlist?.["0"]?.Page;
                if (playlistPlayers) {
                    for (const subPlayer of playlistPlayers) {
                        if (subPlayer.$.type === 'scene') {
                            const oldSrc = subPlayer.Player[0].$.src;
                            const newSrc = await this.importScene(i_businessId, oldSrc, 'imported scene ' + oldSrc, privateChild, showDetails, labelColor);
                            subPlayer.Player[0].$.src = newSrc;

                        } else if (subPlayer.$.type === 'resource') {
                            if (skipIds && skipIds.includes(subPlayer.Player[0].$.id)) continue;
                            const resourceId = subPlayer.Player[0].Data[0].Resource[0].$.resource;
                            const newResourceId = await this.importResource(i_businessId, resourceId);
                            subPlayer.Player[0].Data[0].Resource[0].$.resource = newResourceId;
                        }
                    }
                }

            } else if (player.Data[0].Resource != null) {  // import resource
                const resourceId = player.Data[0].Resource[0].$.resource;
                const newResourceId = await this.importResource(i_businessId, resourceId);
                player.Data[0].Resource[0].$.resource = newResourceId;
            }

            if (player.Data?.[0]?.Json?.[0]?.Player?.[0]?.$?.src) {
                const oldSceneId = player.Data[0].Json[0].Player[0].$.src;
                if (oldSceneId != 'null' && oldSceneId != '-1' && oldSceneId != '0') {
                    const newSceneId = await this.importScene(i_businessId, oldSceneId, 'imported scene ' + oldSceneId);
                    player.Data[0].Json[0].Player[0].$.src = newSceneId;    
                }
            }

            // import scenes & resources of events
            if (player.$.clickEventParam || player.$.selectItemEventParam) {
                const paramKeys = ['clickEventParam', 'selectItemEventParam'];
                for (const paramKey of paramKeys) {
                    if (!player.$[paramKey]) continue;
                    const params = JSON.parse(player.$[paramKey]) || {};

                    if (params.type === 'scene') {
                        const oldSceneId = params.id;
                        const newSceneId = await this.importScene(i_businessId, oldSceneId, 'imported scene ' + oldSceneId, privateChild, showDetails, labelColor);
                        params.id = newSceneId;
                        player.$[paramKey] = JSON.stringify(params);
                    } else if (params.type === 'resource') {
                        const resourceId = params.id;
                        const newResourceId = await this.importResource(i_businessId, resourceId);
                        params.id = newResourceId;
                        player.$[paramKey] = JSON.stringify(params);
                    }
                }
            }

            if (labelColor && player.Data[0].Label && player.Data[0].Label[0]) {
                player.Data[0].Label[0].Font[0].$.fontColor = labelColor;
            }
            if (showDetails) {
                this.progress.next(`${player.$.label}${player.$.id}`);
            }
        }
    }

    async importSceneAsPlayers(i_businessId, sceneId, showDetails = false, skipImportPlayers = false) {
        return new Promise<Array<ISceneBlock>>((resolve, error) => {
            const readRecord: ICRUDRecordRead = {
                businessId: i_businessId,
                id: sceneId,
                table: 'scenes',
                changelist: '-1',
                reqFor: "studio"
            };
            this.appService.crudRecordRead(readRecord).then((i_crudReply: ICRUDRecordReadReply) => {
                xml2js.parseString(i_crudReply.player_data_value, {charkey: '_'}, async (err, sceneData: IPlayerProps) => {
                    const players = sceneData.Player.Data?.[0]?.Scene?.[0]?.Players?.[0]?.Player;
                    if (!players || skipImportPlayers) {
                        resolve(players);
                        return;
                    }

                    for (const player of players) {
                        if (player.$.player == '3510') {
                            const oldSceneId = player.$.src;
                            const newSceneId = await this.importScene(i_businessId, oldSceneId, 'imported scene ' + oldSceneId);
                            player.$.src = newSceneId;
                        } else if (player.Data[0].Resource != null) {
                            const resourceId = player.Data[0].Resource[0].$.resource;
                            const newResourceId = await this.importResource(i_businessId, resourceId);
                            player.Data[0].Resource[0].$.resource = newResourceId;
                        }
                        if (showDetails) {
                            this.progress.next(`${player.$.label}${player.$.id}`);
                        }
                    }

                    resolve(players);
                });
            }).catch((err) => error(err));
        });
    }

    async importResource(i_businessId, resourceId) {
        return new Promise<string>((resolve, error) => {
            const key = i_businessId + '.' + resourceId;
            const localResourceId = this.resourceShortcuts[key];
            if (localResourceId) {
                resolve(localResourceId);
                return;
            }

            const readRecord: ICRUDRecordRead = {
                businessId: i_businessId,
                id: resourceId,
                table: 'resources',
                changelist: '-1',
                reqFor: "studio"
            };

            this.appService.crudRecordRead(readRecord).then((i_crudReply: ICRUDRecordReadReply) => {
                const resourceUrl = appData.cloudResourcesS3 + '/business' + i_businessId + '/resources/' + resourceId + '.' + i_crudReply.resource_type;
                const saveRecord: ICRUDRecord = {
                    studioId: this.localStoreService.getStorageConfig().studioId,
                    command: 'upload',
                    primary: 'resource_id',
                    table: 'resources',
                    ext: i_crudReply.resource_type,
                    body: {
                        url: resourceUrl
                    },
                    id: null
                };

                this.progress.next(i_crudReply.resource_name);

                this.appService.crudRecord(saveRecord).then((i_result: ICRUDRecord) => {
                    if (!i_result.serverReply.status) return error(i_result.serverReply);

                    const newResourceId = String(i_result.serverReply.id);
                    if (SUPPORTED_FILE_TYPES.indexOf(i_crudReply.resource_type.toLowerCase()) === -1) {
                        resolve(null);
                        return;
                    };
                    
                    const updateRecord: IResourceUpload = {
                        resource_id: newResourceId,
                        default_player: this.storeSelector.getBlockLabelByFileType(i_crudReply.resource_type.toLowerCase()).id as any,
                        resource_name: i_crudReply.resource_name,
                        resource_type: i_crudReply.resource_type,
                        resource_date_created: new Date().getTime() + '',
                        resource_date_modified: new Date().getTime() + '',
                        resource_bytes_total: i_crudReply.resource_bytes_total,
                        access_key: '0',
                        shortcut_business_id: i_businessId,
                        shortcut_resource_id: resourceId
                    };

                    const updateResource: ICRUDRecord = {
                        studioId: this.localStoreService.getStorageConfig().studioId,
                        command: 'update',
                        primary: 'resource_id',
                        table: 'resources',
                        body: updateRecord,
                        id: newResourceId
                    };

                    this.appService.crudRecord(updateResource).then((i_crudReply2: ICRUDRecord) => {
                        updateRecord.id = newResourceId;
                        updateRecord.resource_id = newResourceId;
                        updateRecord.changelist_id = String(i_result.serverReply.changelist_id);
                        this.storeSelector.dispatch(actionAddResources({data: updateRecord as IResource}));
                        resolve(newResourceId);
                    });
                }).catch((err) => error(err));
            }).catch((err) => error(err));
        });
    }

    async getRemoteScene(businessId, sceneId) {
        return new Promise<IPlayerProps>((resolve, error) => {
            const readRecord: ICRUDRecordRead = {
                businessId: businessId,
                id: sceneId,
                table: 'scenes',
                changelist: '-1',
                reqFor: "studio"
            };
            this.appService.crudRecordRead(readRecord).then((i_crudReply: ICRUDRecordReadReply) => {
                xml2js.parseString(i_crudReply.player_data_value, {charkey: '_'}, async (err, sceneData: IPlayerProps) => {
                    return resolve(sceneData);
                });
            }).catch((err) => error(err));
        });
    }

    async getRemoteResource(businessId, resourceId) {
        return new Promise<IResource>((resolve, error) => {
            const key = businessId + '.' + resourceId;
            const localResourceId = this.resourceShortcuts[key];
            if (localResourceId) {
                this.cod(
                    this.storeSelector.getResource(localResourceId).pipe(take(1)).subscribe((i_resource: IResource) => {
                        resolve(i_resource);
                    })
                );
                return;
            }

            const readRecord: ICRUDRecordRead = {
                businessId: businessId,
                id: resourceId,
                table: 'resources',
                changelist: '-1',
                reqFor: "studio"
            };

            this.appService.crudRecordRead(readRecord).then((i_crudReply: ICRUDRecordReadReply) => {
                return resolve(i_crudReply as IResource);
            }).catch((err) => error(err));
        });
    }
}
